import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, getDocs, query, orderBy, doc, deleteDoc, addDoc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../../../firebase/config';
import { FaEdit, FaTrash, FaPlus, FaImage, FaStar, FaRegStar } from 'react-icons/fa';
import './News.scss';
import NewsModal from './components/NewsModal';

const News = () => {
  const [news, setNews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showModal, setShowModal] = useState(false);

  const navigate = useNavigate();

  const fetchNews = async () => {
    try {
      setLoading(true);
      const newsCollectionRef = collection(db, 'news');
      const q = query(newsCollectionRef, orderBy('createdAt', 'desc'));
      const querySnapshot = await getDocs(q);
      
      const newsData = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      setNews(newsData);
      setLoading(false);
    } catch (err) {
      console.error('Error fetching news:', err);
      setError('Failed to fetch news. Please try again.');
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchNews();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleAddNewNews = () => {
    setShowModal(true);
  };

  const handleEditNews = (newsItem) => {
    navigate(`/admin/news/edit/${newsItem.id}`);
  };

  const handleDeleteNews = async (newsId, newsTitle) => {
    const confirmMessage = `Are you sure you want to delete the news article "${newsTitle}"? This action cannot be undone.`;
    
    if (window.confirm(confirmMessage)) {
      try {
        await deleteDoc(doc(db, 'news', newsId));
        setNews(news.filter(newsItem => newsItem.id !== newsId));
        
        // Show success message
        setError(null);
        alert(`News article "${newsTitle}" has been successfully deleted.`);
      } catch (err) {
        console.error('Error deleting news:', err);
        setError('Failed to delete news. Please try again.');
      }
    }
  };

  const handleCreateNews = async (newsData) => {
    try {
      const newsCollectionRef = collection(db, 'news');
      const newNewsRef = await addDoc(newsCollectionRef, {
        ...newsData,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        status: 'draft'
      });
      
      setShowModal(false);
      navigate(`/admin/news/edit/${newNewsRef.id}`);
    } catch (err) {
      console.error('Error creating news:', err);
      setError('Failed to create news. Please try again.');
    }
  };

  const handleToggleFeatured = async (newsId, currentFeatured) => {
    try {
      const newsDocRef = doc(db, 'news', newsId);
      const newFeaturedStatus = !currentFeatured;
      
      await updateDoc(newsDocRef, {
        featured: newFeaturedStatus,
        updatedAt: serverTimestamp()
      });
      
      // Update local state
      setNews(prevNews => 
        prevNews.map(newsItem => 
          newsItem.id === newsId 
            ? { ...newsItem, featured: newFeaturedStatus }
            : newsItem
        )
      );
      
      console.log(`News article ${newFeaturedStatus ? 'marked as featured' : 'unmarked as featured'}`);
    } catch (err) {
      console.error('Error toggling featured status:', err);
      setError('Failed to update featured status. Please try again.');
    }
  };

  const renderNewsCard = (newsItem) => {
    return (
      <div className="news-card" key={newsItem.id}>
        <div className="news-card-content">
          <div className="news-image">
            {newsItem.featuredImage ? (
              <img src={newsItem.featuredImage} alt={newsItem.title} />
            ) : (
              <div className="no-image">
                <FaImage style={{ fontSize: '2rem', opacity: 0.5, marginBottom: '8px' }} />
                <span>No Image</span>
              </div>
            )}
          </div>
          <div className="news-details">
            <div className="news-actions">
              <button 
                className={`featured-toggle-btn ${newsItem.featured ? 'featured' : 'not-featured'}`}
                onClick={() => handleToggleFeatured(newsItem.id, newsItem.featured)}
                aria-label={newsItem.featured ? 'Remove from featured' : 'Mark as featured'}
                title={newsItem.featured ? 'Remove from featured' : 'Mark as featured'}
              >
                {newsItem.featured ? <FaStar /> : <FaRegStar />}
                {newsItem.featured ? 'Featured' : 'Feature'}
              </button>
              <button 
                className="edit-btn" 
                onClick={() => handleEditNews(newsItem)}
                aria-label="Edit news"
              >
                <FaEdit /> Edit
              </button>
              <button 
                className="delete-btn" 
                onClick={() => handleDeleteNews(newsItem.id, newsItem.title)}
                aria-label="Delete news"
              >
                <FaTrash /> Delete
              </button>
            </div>
            <h3>{newsItem.title}</h3>
            <div className="news-meta">
              <span className={`status ${newsItem.status || 'draft'}`}>
                {newsItem.status || 'draft'}
              </span>
              {newsItem.category && (
                <span className="category">
                  {newsItem.category}
                </span>
              )}
              <span className="date">
                {newsItem.createdAt ? new Date(newsItem.createdAt.seconds * 1000).toLocaleDateString() : 'No date'}
              </span>
            </div>
            {newsItem.tags && newsItem.tags.length > 0 && (
              <div className="news-tags">
                {newsItem.tags.map((tag, index) => (
                  <span key={index} className="tag">{tag}</span>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="news-management-container">
      <div className="page-header">
        <h1>News Management</h1>
        <p>Create, edit, and manage news articles and announcements.</p>
        <button 
          className="add-news-btn" 
          onClick={handleAddNewNews}
        >
          <FaPlus /> Add New News
        </button>
      </div>

      {error && <div className="error-message">{error}</div>}
      
      {loading ? (
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading news...</p>
        </div>
      ) : news.length === 0 ? (
        <div className="no-news">
          <p>No news articles found. Click "Add New News" to create your first article.</p>
        </div>
      ) : (
        <div className="news-grid">
          {news.map(newsItem => renderNewsCard(newsItem))}
        </div>
      )}

      {showModal && (
        <NewsModal 
          onClose={() => setShowModal(false)} 
          onSubmit={handleCreateNews}
        />
      )}
    </div>
  );
};

export default News; 